import type { HsGraphicLines } from '../../graphic-render-new/objects/graphic-lines.class';
import { HsBox } from '../../math-new/shapes/box.class';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import type { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import { HsColor } from '../../math-new/objects/color.class';
import { HsQuaternion } from '../../math-new/objects/quaternion.class';
import { HsBoxHelper } from '../helpers/box-helper.class';
import { HsPrimitives } from '../primitives/primitives.class';
import { HsTextureMaterial } from '../../graphic-render-new/materials/texture-material.class';
import type { HsDefaultShader } from '../../graphic-render-new/shaders/classes/default-shader.class';
import { HsShaderData } from '../../graphic-render-new/shaders/classes/shader-data.class';
import { HsShaderObjectType } from '../../graphic-render-new/shaders/enums/shader-object-type.enum';
import { HsTextureUtils } from '../../graphic-render-new/textures/texture-utils';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';
import { HsDefaultMaterial } from '../../graphic-render-new/materials/default-material.class';

export class HsControlBox {
  private readonly primitives: HsPrimitives;

  public constructor(private readonly graphics: HsGraphics, private queue: HsGraphicQueue) {
    this.primitives = new HsPrimitives(this.graphics);
    this.primitives.setQueue(this.queue);
    const standardMaterial: HsDefaultMaterial<HsDefaultShader> = this.initStandardMaterial();
    standardMaterial.color = new HsColor(0.75, 0.75, 0.75);
    this.primitives.setMaterial(standardMaterial);
    this.addAngles();
    this.addCorners();
    this.addLines();

    this.initTexturedMaterial().then((texturedMaterial: HsTextureMaterial<HsDefaultShader>) => {
      texturedMaterial.color = new HsColor(0.75, 0.75, 0.75);
      this.addPlanes(texturedMaterial);
    });
  }

  public setQueue(queue: HsGraphicQueue): void {
    this.queue = queue;
  }

  private addAngles(): void {
    const angles: HsQuaternion[] = [
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 0),
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 1),
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 2),
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 3),
      this.makeRotation([0, 1, 0], Math.PI / 2),
      this.makeRotation([0, 1, 0], Math.PI / 2),
      this.makeRotation([0, 1, 0], Math.PI / 2),
      this.makeRotation([0, 1, 0], Math.PI / 2),
      this.makeRotation([0, 0, 1], Math.PI / 2),
      this.makeRotation([0, 0, 1], Math.PI / 2),
      this.makeRotation([0, 0, 1], Math.PI / 2),
      this.makeRotation([0, 0, 1], Math.PI / 2),
    ];
    for (let i: number = 4; i < 8; i++) {
      angles[i].multiply(this.makeRotation([1, 0, 0], (Math.PI / 2) * i));
    }
    for (let i: number = 8; i < 12; i++) {
      angles[i].multiply(this.makeRotation([1, 0, 0], (Math.PI / 2) * i));
    }
    for (const angle of angles) {
      this.primitives.makeAngle([0, 0, 0], angle, undefined, 0.5);
    }
  }

  private addCorners(): void {
    const angles: HsQuaternion[] = [
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 0),
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 1),
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 2),
      this.makeRotation([1, 0, 0], (Math.PI / 2) * 3),
      this.makeRotation([0, 1, 0], Math.PI),
      this.makeRotation([0, 1, 0], Math.PI),
      this.makeRotation([0, 1, 0], Math.PI),
      this.makeRotation([0, 1, 0], Math.PI),
    ];
    for (let i: number = 4; i < 8; i++) {
      angles[i].multiply(this.makeRotation([1, 0, 0], (Math.PI / 2) * i));
    }
    for (const angle of angles) {
      this.primitives.makeCorner([0, 0, 0], angle, undefined, 0.5);
    }
  }

  private addPlanes(texturedMaterial: HsTextureMaterial<HsDefaultShader>): void {
    // top
    texturedMaterial.textureRect = [0.07, 0.23, 0.2, 0.2];
    this.primitives.setMaterial(texturedMaterial);
    this.primitives.makePlaneWithUvs([0, 1, 0], this.makeRotation([0, 1, 0], Math.PI), [0.5, 0.5, 0.5]);
    // bottom
    texturedMaterial = texturedMaterial.makeCopy(this.graphics.gl);
    texturedMaterial.textureRect = [0.64, 0.21, -0.27, 0.27];
    this.primitives.setMaterial(texturedMaterial);
    this.primitives.makePlaneWithUvs([0, -1, 0], this.makeRotation([0, 1, 0], 0), [0.5, 0.5, 0.5]);
    // left
    texturedMaterial = texturedMaterial.makeCopy(this.graphics.gl);
    texturedMaterial.textureRect = [0.69, 0.47, 0.27, -0.27];
    this.primitives.setMaterial(texturedMaterial);
    this.primitives.makePlaneWithUvs([1, 0, 0], this.makeRotation([0, 0, 1], Math.PI / 2), [0.5, 0.5, 0.5]);
    // right
    texturedMaterial = texturedMaterial.makeCopy(this.graphics.gl);
    texturedMaterial.textureRect = [0.04, 0.81, 0.27, -0.27];
    this.primitives.setMaterial(texturedMaterial);
    this.primitives.makePlaneWithUvs([-1, 0, 0], this.makeRotation([0, 0, 1], -Math.PI / 2), [0.5, 0.5, 0.5]);
    // front
    texturedMaterial = texturedMaterial.makeCopy(this.graphics.gl);
    texturedMaterial.textureRect = [0.65, 0.53, -0.27, 0.27];
    this.primitives.setMaterial(texturedMaterial);
    this.primitives.makePlaneWithUvs([0, 0, -1], this.makeRotation([1, 0, 0], Math.PI / 2), [0.5, 0.5, 0.5]);
    // back
    texturedMaterial = texturedMaterial.makeCopy(this.graphics.gl);
    texturedMaterial.textureRect = [0.71, 0.53, 0.27, 0.27];
    this.primitives.setMaterial(texturedMaterial);
    this.primitives.makePlaneWithUvs([0, 0, 1], this.makeRotation([1, 0, 0], Math.PI / 2), [0.5, 0.5, 0.5]);
  }

  private addLines(): void {
    const boxHelper: HsBoxHelper = new HsBoxHelper(this.graphics, HsBox.makeUnitCube(), HsColor.makeBlack());
    const lines: HsGraphicLines = boxHelper.makeLines();
    const cubeScale: number = 2.01;
    lines.worldMatrix.scale([cubeScale, cubeScale, cubeScale]);
    lines.updateBoundingSphere();
    this.queue.addObject(lines);
  }

  private async initTexturedMaterial(): Promise<HsTextureMaterial<HsDefaultShader>> {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth().addTexture().addNormals().addRayIntersection().addSpecularLighting();
    const shader: HsDefaultShader = shaderData.makeShader();
    const texturedMaterial: HsTextureMaterial<HsDefaultShader> = new HsTextureMaterial(this.graphics.gl, shader);
    const texture: WebGLTexture | null = await HsTextureUtils.load(this.graphics.gl, '/assets/img/control-box.png');
    texturedMaterial.texture = texture;
    return texturedMaterial;
  }

  private initStandardMaterial(): HsDefaultMaterial<HsDefaultShader> {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth().addNormals().addDiffuseLighting();
    const shader: HsDefaultShader = shaderData.makeShader();
    return new HsDefaultMaterial(this.graphics.gl, shader);
  }

  private makeRotation(axis: HsHypersceneTypes.Array3, angle: number): HsQuaternion {
    return HsQuaternion.makeFromAxisAndAngle(axis, angle);
  }
}
